import DetailsSidebar from '../../component/detailssidebar/DetailsSidebar';
import { EventConfigService } from '../../service/EventConfigService';
import useValidator from '../../utils/validator/useValidator';
import { useContext, useEffect, useRef, useState } from 'react';
import { MessageContext } from '../../context/MessageContext';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../component/input/Dropdown';
import InputNumber from '../../component/input/InputNumber';
import InputText from '../../component/input/InputText';
import './EventConfigSidebar.scss';



const EventConfigSidebar = ({ visible, onHide, onSuccess, eventConfigId }) => {

    const emptyForm = {
        id: null,
        type: '',
        threshold: null,
        priority: null,
        generateServiceTask: false,
        manualTaskType: null,
        daysToArchive: 30,
        status: null,
        displayOnErrorList: false
    };

    const { t } = useTranslation();
    const validator = useValidator();
    const { showErrorMessage, showSuccessMessage } = useContext(MessageContext);
    const isMounted = useRef(false);
    const [mainKey, setMainKey] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [form, setForm] = useState({ ...emptyForm });
    const [priorityOptions, setCriticalityOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [taskTypeOptions, setTaskTypeOptions] = useState([]);


    const forceUpdate = () => {
        setMainKey(prev => prev + 1);
    }

    const fetchPriorities = () => {
        EventConfigService.getEventPriorityList().then(priorities => {
            if (isMounted.current) {
                setCriticalityOptions(priorities.map(priority => ({
                    id: priority,
                    label: t(`eventConfig.priority.${priority}`)
                })));
            }
        }).catch(err => {
            showErrorMessage(t('eventConfigSidebar.error.fetchPriorities'));
        });
    }

    const fetchStatuses = () => {
        EventConfigService.getStatuses().then(statuses => {
            if (isMounted.current) {
                setStatusOptions(statuses.map(status => ({ id: status, label: t(`eventConfig.statuses.${status}`) })));
            }
        }).catch(err => {
            showErrorMessage(t('eventConfigSidebar.error.fetchStatuses'));
        });
    }

    const fetchManualTaskTypes = () => {
        EventConfigService.getManualTaskTypes().then(types => {
            if (isMounted.current) {
                setTaskTypeOptions(types.map(type => ({
                    id: type,
                    label: t(`ManualTaskType.${type}`)
                })));
            }
        }).catch(err => {
            showErrorMessage(t('eventConfigSidebar.error.fetchTypes'));
        });
    }
    const fetchEventConfig = (id) => {
        EventConfigService.getEventConfig(id).then(config => {
            if (isMounted.current) {
                setForm({
                    ...config,
                    id: config.externalID,
                    type: config.eventType ? t(`EventType.${config.eventType}`) : '',
                    eventType: config.eventType,
                    priority: config.priority ? { id: config.priority } : null,
                    status: config.status ? { id: config.status } : null,
                    generateServiceTask: config.generateServiceTask,
                    manualTaskType: config.manualTaskType ? { id: config.manualTaskType } : null,
                    displayOnErrorList: config.displayList || config.displayOnErrorList
                });
                setIsLoading(false);
            }
        }).catch(err => {
            showErrorMessage(t('eventConfigSidebar.error.fetchConfig'));
            setIsLoading(false);
        });
    }

    useEffect(() => {
        isMounted.current = true;
        fetchPriorities();
        fetchStatuses();
        fetchManualTaskTypes();
        return () => {
            isMounted.current = false;
        }
    }, [t]);



    useEffect(() => {
        if (visible) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [visible]);


    const clearForm = () => {
        setForm({ ...emptyForm });
    }


    const handleOnShow = () => {
        if (eventConfigId) {
            setIsLoading(true);
            fetchEventConfig(eventConfigId);
        }
        validator.hideMessages();
        forceUpdate();
    }


    const handleOnHide = () => {
        validator.hideMessages();
        clearForm();
        forceUpdate();
        if (onHide) {
            onHide();
        }
    }

    const save = () => {
        setIsSaving(true);
        EventConfigService.updateEventConfig(form).then(() => {
            showSuccessMessage(t('eventConfig.success.update'));
            handleOnHide();
            if (onSuccess) {
                onSuccess();
            }
            setIsSaving(false);
        }).catch(err => {
            showErrorMessage(t('eventConfig.error.update'));
            setIsSaving(false);
        });

    }
    const handleSave = () => {
        if (validator.allValid()) {
            save();
        } else {
            validator.showMessages();
            forceUpdate();
        }
    };

    const title = t('eventConfig.editDialogTitle');

    return (
        <DetailsSidebar title={title} visible={visible} onHide={handleOnHide} onShow={handleOnShow} onSave={handleSave} contentKey={mainKey} isSaving={isSaving} isLoading={isLoading}>
            <InputText
                id='type'
                label={t('eventConfig.type')}
                value={form.type}
                validator={validator}
                validations="required"
                disabled={!!eventConfigId}
                onChange={e => setForm({ ...form, type: e.target.value })}
            />

            <InputNumber
                id='threshold'
                label={t('eventConfig.threshold')}
                validator={validator}
                validations="numeric|number_max:2000000000|number_min:-2000000000"
                value={form.threshold}
                onChange={(e) => setForm({ ...form, threshold: e.value })} />

            <Dropdown
                id='criticality'
                label={t('eventConfig.priority_key')}
                options={priorityOptions}
                dataKey='id'
                optionLabel='label'
                validator={validator}
                validations="required"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.value })} />
            <Dropdown
                id='generateServiceTask'
                label={t('eventConfig.generateServiceTask')}
                options={[
                    { id: true, label: t('Boolean.true') },
                    { id: false, label: t('Boolean.false') }
                ]}
                optionValue='id'
                validator={validator}
                validations="required"
                value={form.generateServiceTask}
                onChange={(e) =>  setForm({...form,
                        generateServiceTask: e.value,
                        
                    })
                } />

            {form.generateServiceTask && (
                <Dropdown
                    id='manualTaskType'
                    dataKey='id'
                    optionLabel='label'
                    label={t('routes.configuration.manualTaskTemplates.taskType')}
                    options={taskTypeOptions}
                    validator={validator}
                    validations="required"
                    value={form.manualTaskType}
                    onChange={(e) => setForm({ ...form, manualTaskType: e.value })}
                />
            )}

            <InputNumber
                id='daysToArchive'
                label={t('eventConfig.daysToArchive')}
                validator={validator}
                validations="numeric|number_max:3650|number_min:0"
                value={form.daysToArchive}
                onChange={(e) => setForm({ ...form, daysToArchive: e.value })} />

<Dropdown
                id='displayOnErrorList'
                label={t('eventConfigSidebar.displayOnErrorList')}
                options={[
                    { id: true, label: t('Boolean.true') },
                    { id: false, label: t('Boolean.false') }
                ]}
                dataKey='id'
                optionLabel='label'
                validator={validator}
                validations="required"
                value={form.displayOnErrorList ? { id: true } : { id: false }}
                onChange={(e) => setForm({ ...form, displayOnErrorList: e.value.id })} />

            <Dropdown
                id='Status'
                dataKey='id'
                optionLabel='label'
                label={t('eventConfig.status')}
                options={statusOptions}
                validator={validator}
                validations="required"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.value })} />

  
        </DetailsSidebar>
    );
};

export default EventConfigSidebar;